/**
 * Repasses (spec 013, item 1, 031 e 048): o pedido de repasse de cada plano na Mesa Real e o que ja foi pago, com o
 * total. Os de antes da mudanca vieram do sistema antigo, que nao guardava o plano (1.9).
 *
 * O pedido (048): o cliente pede o total, ou parcial dizendo quanto quer deixar de margem, ou negocia no WhatsApp. Um
 * administrador le o lucro na Nelogica e manda o calculo; o cliente confere e confirma (ou diz que nao concorda), e a
 * area mostra "Aguardando repasse" ate o PIX ser registrado. Quando nao da pra pedir, a tela **diz por que** em vez de
 * esconder o botao (031, secao 6).
 */
import { Aviso, Botao, Campo, CampoDinheiro, CampoTextoLongo, Carregando, Cartao, ContaDoRepasse, Dialogo, formatarDia, formatarReais, LinkInterno, Pilha, Segmentado, Tabela, Vazio, type ContaDoRepasse as Conta } from "@mtc/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { useAcao, useApi } from "../acoes";
import { useDentroDeAssunto } from "../assunto";
import { marca } from "../config";
import { useSessao } from "../sessao";
import { usePagina } from "../pagina";

type Repasse = { id: string; plano: string | null; antes_da_mudanca: boolean; pago_em: string; valor_liquido_centavos: number; chave_pix: string; mensagem: string | null; comprovante: { nome: string; tipo: string; tamanho: number } | null };
type Margem = { tipo: "VALOR"; centavos: number } | { tipo: "PERCENTUAL"; percentual: number };
type Pedido = { id: string; modo: "TOTAL" | "PARCIAL" | "WHATSAPP"; etapa: "NOVO" | "ESPERANDO_CLIENTE" | "CLIENTE_NAO_CONCORDOU" | "A_PAGAR"; margem: Margem | null; calculo: Conta | null; nao_concordou: { motivo: string } | null; chave_pix: string | null; chave_congelada: boolean; mensagem: string | null; criada_em: string };
type PlanoDoRepasse = { matricula_id: string; plano: string; pode_pedir: boolean; motivo: string | null; pedido: Pedido | null };

/** O WhatsApp da marca com a mensagem pronta. Sem numero configurado, o botao nao aparece. */
function whatsapp(texto: string): string | null {
  const w = marca().whatsapp;
  return w ? `${w}${w.includes("?") ? "&" : "?"}text=${encodeURIComponent(texto)}` : null;
}

const margemEmPalavras = (m: Margem | null) => (!m ? "" : m.tipo === "VALOR" ? `deixando ${formatarReais(m.centavos)} de margem` : `deixando ${m.percentual}% do lucro de margem`);

/** Mesmo idioma dos outros dialogos da area (ver `Planos.tsx`): `Dialogo` + `useAcao` + campos do design system. */
function PedirRepasse({ p, modo, aoFechar }: { p: PlanoDoRepasse; modo: "TOTAL" | "PARCIAL"; aoFechar: () => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [tipo, setTipo] = useState<"VALOR" | "PERCENTUAL">("VALOR");
  const [centavos, setCentavos] = useState(0);
  const [percentual, setPercentual] = useState("");
  const [mensagem, setMensagem] = useState("");
  useEffect(() => acao.limpar(), [modo]);
  const pct = Number(percentual.replace(",", "."));
  const pronto = modo === "TOTAL" || (tipo === "VALOR" ? centavos > 0 : Number.isInteger(pct) && pct >= 1 && pct <= 99);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    const margem = modo === "PARCIAL" ? (tipo === "VALOR" ? { tipo, centavos } : { tipo, percentual: pct }) : undefined;
    const ok = await acao.rodar(() => postar("/v1/me/repasses/pedidos", { matricula_id: p.matricula_id, modo, ...(margem ? { margem } : {}), mensagem: mensagem.trim() || undefined }));
    if (!ok) return;
    await qc.invalidateQueries();
    aoFechar();
  };
  return (
    <Dialogo aberto aoFechar={aoFechar} titulo={modo === "TOTAL" ? "Repasse total" : "Repasse parcial"} descricao={modo === "TOTAL" ? `Do plano ${p.plano}. A gente lê o lucro da sua conta na Nelogica e te manda o cálculo pra você conferir antes do PIX.` : `Do plano ${p.plano}. Diga quanto quer deixar na conta. O resto do lucro vira o seu repasse, e você confere o cálculo antes do PIX.`}>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        {modo === "PARCIAL" && (
          <>
            <Segmentado rotulo="Como você quer dizer a margem" nome="margem" valor={tipo} aoMudar={setTipo} opcoes={[{ valor: "VALOR", rotulo: "Valor fixo" }, { valor: "PERCENTUAL", rotulo: "Porcentagem" }]} />
            {tipo === "VALOR"
              ? <CampoDinheiro rotulo="Quanto deixar de margem na conta" centavos={centavos} aoMudar={setCentavos} dica="Esse valor fica na conta. O resto do lucro é o seu repasse." />
              : <Campo rotulo="Quanto do lucro deixar de margem (%)" valor={percentual} aoMudar={(v) => setPercentual(v.replace(/[^\d]/g, "").slice(0, 2))} inputMode="numeric" placeholder="Ex.: 30" dica="De 1% a 99%. O resto do lucro é o seu repasse." />}
          </>
        )}
        <CampoTextoLongo rotulo="Recado pra gente (opcional)" valor={mensagem} aoMudar={setMensagem} maxLength={500} placeholder="Ex.: preciso até sexta" />
        <div className="mtc-dialogo__rodape">
          <Botao type="button" variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" disabled={!pronto} carregando={acao.carregando}>Pedir repasse</Botao>
        </div>
      </form>
    </Dialogo>
  );
}

function NaoConcordo({ pedido, aoFechar }: { pedido: Pedido; aoFechar: () => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [motivo, setMotivo] = useState("");
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (await acao.rodar(() => postar(`/v1/me/repasses/pedidos/${pedido.id}/nao-concordo`, { motivo: motivo.trim() }))) { await qc.invalidateQueries(); aoFechar(); }
  };
  return (
    <Dialogo aberto aoFechar={aoFechar} titulo="Não concordo com o cálculo" descricao="Conte o que não bate. A gente revê e te manda outro cálculo.">
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <CampoTextoLongo rotulo="O que não bate" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder="Ex.: o lucro na minha conta é maior" />
        <div className="mtc-dialogo__rodape">
          <Botao type="button" variante="fantasma" onClick={aoFechar}>Voltar</Botao>
          <Botao type="submit" disabled={motivo.trim().length < 5} carregando={acao.carregando}>Enviar</Botao>
        </div>
      </form>
    </Dialogo>
  );
}

/** O pedido em andamento, no passo em que esta. */
function PedidoEmAndamento({ p, pedido, chaveDoPerfil }: { p: PlanoDoRepasse; pedido: Pedido; chaveDoPerfil: string | null }) {
  const { postar, apagar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [naoConcordo, setNaoConcordo] = useState(false);
  const desistir = useMutation({ mutationFn: () => apagar(`/v1/me/repasses/pedidos/${pedido.id}`), onSuccess: () => void qc.invalidateQueries() });
  const confirmar = async () => { if (await acao.rodar(() => postar(`/v1/me/repasses/pedidos/${pedido.id}/confirmar`, {}))) await qc.invalidateQueries(); };
  const oQuePediu = pedido.modo === "TOTAL" ? "o repasse total" : pedido.modo === "PARCIAL" ? `o repasse parcial, ${margemEmPalavras(pedido.margem)}` : "o repasse combinado no WhatsApp";
  const botaoDesistir = <Botao tamanho="pequeno" variante="fantasma" disabled={desistir.isPending} onClick={() => desistir.mutate()}>Desistir do pedido</Botao>;

  if (pedido.etapa === "A_PAGAR") {
    return <Aviso tipo="sucesso">Aguardando repasse. Você confirmou <strong className="mtc-mono">{pedido.calculo ? formatarReais(pedido.calculo.liquido_centavos) : ""}</strong> pra chave <strong className="mtc-mono">{pedido.chave_pix}</strong>. Quando o PIX sair, ele aparece na lista abaixo.</Aviso>;
  }
  if (pedido.etapa === "ESPERANDO_CLIENTE" && pedido.calculo) {
    const conversa = pedido.modo === "WHATSAPP" ? whatsapp(`Olá! Recebi o cálculo do repasse do plano ${p.plano} e quero conversar.`) : null;
    return (
      <Pilha espaco={3}>
        <strong>Confira o cálculo do seu repasse</strong>
        <ContaDoRepasse conta={pedido.calculo} />
        {chaveDoPerfil
          ? <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>O PIX vai pra chave <strong className="mtc-mono">{chaveDoPerfil}</strong>, a do seu Perfil. <LinkInterno className="mtc-link" href="/perfil">Trocar no Perfil</LinkInterno></span>
          : <Aviso tipo="alerta">Cadastre sua chave PIX no <LinkInterno className="mtc-link" href="/perfil">Perfil</LinkInterno> pra confirmar o repasse.</Aviso>}
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap" }}>
          <Botao tamanho="pequeno" disabled={!chaveDoPerfil} carregando={acao.carregando} onClick={() => void confirmar()}>Confirmar</Botao>
          {pedido.modo === "WHATSAPP"
            ? conversa && <a href={conversa} target="_blank" rel="noreferrer" className="mtc-botao mtc-botao--secundario mtc-botao--pequeno">Não concordo</a>
            : <Botao tamanho="pequeno" variante="secundario" onClick={() => setNaoConcordo(true)}>Não concordo</Botao>}
          {botaoDesistir}
        </div>
        {naoConcordo && <NaoConcordo pedido={pedido} aoFechar={() => setNaoConcordo(false)} />}
      </Pilha>
    );
  }
  if (pedido.etapa === "CLIENTE_NAO_CONCORDOU") {
    return <Pilha espaco={3}><Aviso tipo="info">Você não concordou com o cálculo{pedido.nao_concordou ? ` ("${pedido.nao_concordou.motivo}")` : ""}. Estamos revendo e te mandamos outro aqui.</Aviso><div>{botaoDesistir}</div></Pilha>;
  }
  return <Pilha espaco={3}><Aviso tipo="info">Estamos avaliando sua solicitação: você pediu {oQuePediu}. O cálculo chega aqui pra você conferir antes do PIX.</Aviso><div>{botaoDesistir}</div></Pilha>;
}

function PedidosDeRepasse() {
  const { ler } = useApi();
  const [pedindo, setPedindo] = useState<{ p: PlanoDoRepasse; modo: "TOTAL" | "PARCIAL" } | null>(null);
  const q = useQuery({ queryKey: ["repasses", "planos"], queryFn: () => ler<{ planos: PlanoDoRepasse[]; chave_pix_do_perfil: string | null }>("/v1/me/repasses/planos") });
  if (!q.data?.planos.length) return null;
  return (
    <Pilha espaco={3}>
      {q.data.planos.map((p) => {
        const negociar = whatsapp(`Olá! Quero negociar o repasse do plano ${p.plano}.`);
        return (
          <Cartao key={p.matricula_id}>
            <Pilha espaco={3}>
              <span className="mtc-rotulo">Repasse · {p.plano}</span>
              {p.pedido ? (
                <PedidoEmAndamento p={p} pedido={p.pedido} chaveDoPerfil={q.data.chave_pix_do_perfil} />
              ) : p.pode_pedir ? (
                <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap" }}>
                  <Botao tamanho="pequeno" onClick={() => setPedindo({ p, modo: "TOTAL" })}>Repasse total</Botao>
                  <Botao tamanho="pequeno" variante="secundario" onClick={() => setPedindo({ p, modo: "PARCIAL" })}>Repasse parcial</Botao>
                  {negociar && <a href={negociar} target="_blank" rel="noreferrer" className="mtc-botao mtc-botao--fantasma mtc-botao--pequeno">Negociar no WhatsApp</a>}
                </div>
              ) : (
                // A tela diz o motivo em vez de sumir com o botao (031, secao 6).
                <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{p.motivo}</span>
              )}
            </Pilha>
          </Cartao>
        );
      })}
      {pedindo && <PedirRepasse p={pedindo.p} modo={pedindo.modo} aoFechar={() => setPedindo(null)} />}
    </Pilha>
  );
}

export function PaginaRepasses() {
  const pag = usePagina();
  const { ler } = useApi();
  const dentroDeAssunto = useDentroDeAssunto();
  const q = useQuery({ queryKey: ["repasses"], queryFn: () => ler<{ itens: Repasse[]; total_centavos: number }>("/v1/me/repasses") });
  // O comprovante do PIX que a equipe anexou (spec 049): fica guardado aqui, pra baixar quando quiser.
  const { arquivo } = useSessao();
  const [baixando, setBaixando] = useState<string>();
  const [erroAoBaixar, setErroAoBaixar] = useState<string>();
  const baixar = async (r: Repasse) => {
    setErroAoBaixar(undefined);
    setBaixando(r.id);
    try {
      const url = URL.createObjectURL(await arquivo(`/v1/me/repasses/${r.id}/comprovante`));
      const a = document.createElement("a");
      a.href = url;
      a.download = r.comprovante?.nome ?? "comprovante";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setErroAoBaixar("Não consegui baixar o comprovante agora. Tente de novo em instantes.");
    } finally {
      setBaixando(undefined);
    }
  };
  return (
    <Pilha espaco={5}>
      {!dentroDeAssunto && <h1 className="mtc-titulo mtc-cabecalho__titulo">Repasses</h1>}
      <PedidosDeRepasse />
      {q.isLoading ? (
        <Carregando compacto texto="Buscando seus repasses" />
      ) : q.isError || !q.data ? (
        <Aviso tipo="erro">Não consegui buscar seus repasses agora. Tente de novo em instantes.</Aviso>
      ) : !q.data.itens.length ? (
        <Vazio titulo="Nenhum repasse por aqui ainda">Quando um repasse for pago, ele aparece nesta lista com o valor e o dia.</Vazio>
      ) : (
        <>
          <Cartao>
            <div style={{ display: "grid", gap: 2 }}>
              <span className="mtc-rotulo">Total recebido</span>
              <strong className="mtc-mono" style={{ fontSize: "var(--t-xl)" }}>{formatarReais(q.data.total_centavos)}</strong>
              <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{q.data.itens.length} {q.data.itens.length === 1 ? "repasse" : "repasses"}</span>
            </div>
          </Cartao>
          {erroAoBaixar && <Aviso tipo="erro">{erroAoBaixar}</Aviso>}
          <Tabela
            linhas={q.data.itens}
            chave={(r) => r.id}
            paginacao={pag}
            colunas={[
              { chave: "dia", rotulo: "Dia", celula: (r) => <span className="mtc-mono">{formatarDia(r.pago_em)}</span> },
              { chave: "plano", rotulo: "Plano", celula: (r) => <>{r.plano ?? (r.antes_da_mudanca ? <span className="mtc-texto-2">Antes da mudança de sistema</span> : "")}{r.mensagem && <span className="mtc-tabela__apoio">{r.mensagem}</span>}</> },
              { chave: "valor", rotulo: "Valor", alinhar: "direita", celula: (r) => <span className="mtc-mono">{formatarReais(r.valor_liquido_centavos)}</span> },
              { chave: "chave", rotulo: "Chave PIX", celula: (r) => <span className="mtc-mono">{r.chave_pix}</span> },
              { chave: "comprovante", rotulo: "Comprovante", celula: (r) => (r.comprovante ? <Botao tamanho="pequeno" variante="secundario" carregando={baixando === r.id} onClick={() => void baixar(r)}>Baixar comprovante</Botao> : <span className="mtc-texto-2">Sem comprovante</span>) },
            ]}
          />
        </>
      )}
    </Pilha>
  );
}
